import BigInteger from "amazon-cognito-identity-js/lib/BigInteger";
import { AuthenticationHelper, DateHelper } from "amazon-cognito-identity-js/lib/index";
import CryptoJS from "crypto-js/core";
import Base64 from "crypto-js/enc-base64";
import Utf8 from "crypto-js/enc-utf8";
import HmacSHA256 from "crypto-js/hmac-sha256";

export interface StartSRPAuthParams {
  authHelper: AuthenticationHelper;
  clientAValue: string;
}

/**
 * Returns authentication parameters for the first of two API calls needed
 * to complete authentication via the SRP protocol.
 */
export function getStartSRPAuthParams(): Promise<StartSRPAuthParams> {
  const authHelper = new AuthenticationHelper();
  return new Promise((resolve, reject) => {
    authHelper.getLargeAValue((err, clientAValue) => {
      if (err) { return reject(err); }
      if (!clientAValue) {
        return reject(new Error("Unable to calculate client A value"));
      }

      return resolve({ authHelper, clientAValue: clientAValue.toString(16) });
    });
  });
}

export interface FinishSRPAuthParams {
  secretBlock: string;
  signature: string;
  timestamp: string;
}

/**
 * Returns authentication parameters for the second of two API calls needed
 * to complete authentication via the SRP protocol.
 *
 * Expects the same instance of `authHelper` returned by `getStartSRPAuthParams`, and
 * `authValues` returned by the server from the first API call.
 */
export function getFinishSRPAuthParams(authHelper: AuthenticationHelper, authValues: {
  userId: string;
  password: string;
  serverBValue: string;
  salt: string;
  secretBlock: string;
  poolName: string;
}): Promise<FinishSRPAuthParams> {
  const { userId, password, serverBValue, salt, secretBlock, poolName } = authValues;
  authHelper.poolName = poolName;
  const dateHelper = new DateHelper();
  const serverBValueInt = new BigInteger(serverBValue, 16);
  const saltInt = new BigInteger(salt, 16);
  return new Promise((resolve, reject) => {
    authHelper.getPasswordAuthenticationKey(
      userId,
      password,
      serverBValueInt,
      saltInt,
      (err, hkdf) => {
        if (err) { return reject(err); }

        const timestamp = dateHelper.getNowString();
        const message = CryptoJS.lib.WordArray.create()
          .concat(Utf8.parse(authHelper.poolName))
          .concat(Utf8.parse(userId))
          .concat(Base64.parse(secretBlock))
          .concat(Utf8.parse(timestamp));
        const key = CryptoJS.lib.WordArray.create(hkdf);
        const signature = Base64.stringify(HmacSHA256(message, key));

        return resolve({ secretBlock, signature, timestamp });
      },
    );
  });
}
